import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export const GuestAccountCard = ({ orderId }) => {
  const { registerGuest } = useAuth();
  const [order, setOrder] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("form");

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data.order)).catch(() => {});
  }, [orderId]);

  const email = order?.shipping_address?.email;
  if (!email || (state === "form" && !String(order.user_id || "").startsWith("guest_"))) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const data = await registerGuest({ order_id: orderId, password, name: order.shipping_address.name });
      setState("done");
      toast.success(`Account created — ${data.claimed} order(s) saved to your history`);
    } catch (err) {
      if (err.response?.status === 409) setState("exists");
      else toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  if (state === "done") {
    return (
      <div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-left" data-testid="guest-account-done">
        <div className="flex items-center gap-2 font-bold text-emerald-800"><CheckCircle2 className="w-5 h-5" /> You're all set!</div>
        <p className="text-sm text-emerald-700 mt-1">This order is saved in <Link to="/orders" className="underline font-semibold">My orders</Link>.</p>
      </div>
    );
  }
  if (state === "exists") {
    return (
      <div className="mt-8 p-5 rounded-2xl bg-indigo-50 border border-indigo-100 text-left" data-testid="guest-account-exists">
        <p className="text-sm text-slate-700">You already have an account with <strong>{email}</strong>.</p>
        <Link to="/login?redirect=/orders"><Button className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="guest-account-signin-btn">Sign in to save this order</Button></Link>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-8 p-5 rounded-2xl bg-white border border-slate-200 text-left shadow-sm" data-testid="guest-account-card">
      <div className="flex items-center gap-2 font-bold text-slate-900"><UserPlus className="w-5 h-5 text-indigo-600" /> Save your order history</div>
      <p className="text-sm text-slate-600 mt-1">Create an account for <strong data-testid="guest-account-email">{email}</strong>. Just pick a password.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <Input type="password" placeholder="Choose a password (6+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-full" data-testid="guest-account-password" />
        <Button type="submit" disabled={busy} className="rounded-full bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="guest-account-create-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </div>
    </form>
  );
};
